import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { 
  MapPin, Users, Zap, Heart, Coffee, TrendingUp,
  ArrowRight, Briefcase, Sparkles, Rocket, Check,
  Building2, Star, ChevronRight
} from 'lucide-react';

const CareersPage = () => {
  const { language } = useLanguage();

  const perks = [
    { icon: MapPin, title: language === 'nl' ? 'Hybride werken' : 'Hybrid working', desc: language === 'nl' ? 'Werk vanuit huis of kantoor' : 'Work from home or office' },
    { icon: TrendingUp, title: language === 'nl' ? 'Groei budget' : 'Growth budget', desc: language === 'nl' ? '€2.000/jaar voor ontwikkeling' : '€2,000/year for development' },
    { icon: Users, title: language === 'nl' ? 'Jong team' : 'Young team', desc: language === 'nl' ? 'Gemiddelde leeftijd 28 jaar' : 'Average age 28 years' },
    { icon: Coffee, title: language === 'nl' ? 'Lunch & snacks' : 'Lunch & snacks', desc: language === 'nl' ? 'Gratis op kantoor' : 'Free at the office' },
    { icon: Heart, title: language === 'nl' ? 'Gezondheid' : 'Health', desc: language === 'nl' ? 'Sportbudget & coaching' : 'Sports budget & coaching' },
    { icon: Rocket, title: language === 'nl' ? 'Startup energie' : 'Startup energy', desc: language === 'nl' ? 'Snel schakelen, impact maken' : 'Move fast, make impact' }
  ];

  const openings = [
    {
      title: 'Senior Full-Stack Developer',
      department: 'Engineering',
      location: 'Amsterdam / Remote',
      type: 'Full-time',
      urgent: true
    },
    {
      title: 'Product Designer',
      department: 'Design',
      location: 'Amsterdam',
      type: 'Full-time',
      urgent: false
    },
    {
      title: 'Customer Success Manager',
      department: 'Operations',
      location: 'Amsterdam',
      type: 'Full-time',
      urgent: false
    },
    {
      title: 'Growth Marketing Manager',
      department: 'Marketing',
      location: 'Amsterdam / Remote',
      type: 'Full-time',
      urgent: true
    },
    {
      title: 'Data Analyst',
      department: 'Data',
      location: 'Amsterdam',
      type: 'Full-time',
      urgent: false
    }
  ];

  const values = [
    {
      icon: Sparkles,
      title: language === 'nl' ? 'Impact driven' : 'Impact driven',
      desc: language === 'nl' 
        ? 'Elke dag helpen we mensen hun droomwoning te vinden. Dat geeft energie!'
        : 'Every day we help people find their dream home. That gives energy!'
    },
    {
      icon: Zap,
      title: language === 'nl' ? 'Move fast' : 'Move fast',
      desc: language === 'nl'
        ? 'We werken snel, itereren vaak en zijn niet bang om te falen.'
        : 'We work fast, iterate often and are not afraid to fail.'
    },
    {
      icon: Users,
      title: language === 'nl' ? 'Team first' : 'Team first',
      desc: language === 'nl'
        ? 'We winnen samen en verliezen samen. Iedereen heeft een stem.'
        : 'We win together and lose together. Everyone has a voice.'
    },
    {
      icon: Heart,
      title: language === 'nl' ? 'Love what you do' : 'Love what you do',
      desc: language === 'nl'
        ? 'Passie is onze drijfveer. We zijn geobsedeerd door het beste product.'
        : 'Passion is our driving force. We are obsessed with the best product.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F7F7]" data-testid="careers-page">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 px-4 overflow-hidden bg-gradient-to-br from-[#1F3A5F] via-[#2E7D32] to-[#1F3A5F]">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-10 left-[10%] w-72 h-72 bg-white rounded-full blur-[100px]"></div>
            <div className="absolute bottom-10 right-[10%] w-96 h-96 bg-[#2E7D32] rounded-full blur-[120px]"></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md rounded-full px-5 py-2.5 mb-6">
              <Briefcase className="w-5 h-5 text-white" />
              <span className="text-white font-semibold text-sm tracking-wide">CARRIÈRE</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
              {language === 'nl' ? 'Bouw mee aan de' : 'Build the'}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white to-[#90EE90]">
                {language === 'nl' ? 'toekomst van wonen' : 'future of housing'}
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 max-w-2xl mx-auto mb-10">
              {language === 'nl' 
                ? 'Word onderdeel van een team dat het verschil maakt voor duizenden mensen op zoek naar hun droomwoning.'
                : 'Become part of a team that makes a difference for thousands of people looking for their dream home.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => document.getElementById('vacatures').scrollIntoView({ behavior: 'smooth' })}
                className="bg-white text-[#1F3A5F] hover:bg-white/90 h-14 px-8 text-lg font-bold rounded-full"
              >
                {language === 'nl' ? 'Bekijk vacatures' : 'View vacancies'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-16 max-w-xl mx-auto">
              <div className="text-center">
                <div className="text-4xl font-black text-white">5</div>
                <div className="text-white/60 text-sm">{language === 'nl' ? 'Teamleden' : 'Team members'}</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-black text-white">1.2K+</div>
                <div className="text-white/60 text-sm">{language === 'nl' ? 'Gebruikers' : 'Users'}</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-black text-white">4.8</div>
                <div className="text-white/60 text-sm">{language === 'nl' ? 'Rating' : 'Rating'}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Perks Section */}
        <section className="py-16 md:py-24 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-black text-[#2A2A2A] mb-4">
                {language === 'nl' ? 'Waarom werken bij Swipe je woning?' : 'Why work at Swipe je woning?'}
              </h2>
              <p className="text-lg text-[#9E9E9E] max-w-2xl mx-auto">
                {language === 'nl' 
                  ? 'Wij geloven dat gelukkige mensen het beste werk leveren'
                  : 'We believe happy people deliver the best work'}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {perks.map((perk, i) => (
                <div key={i} className="bg-[#F7F7F7] rounded-2xl p-6 hover:shadow-lg transition-all hover:-translate-y-1">
                  <div className="w-14 h-14 bg-[#2E7D32]/10 rounded-2xl flex items-center justify-center mb-4">
                    <perk.icon className="w-7 h-7 text-[#2E7D32]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#2A2A2A] mb-2">{perk.title}</h3>
                  <p className="text-[#9E9E9E]">{perk.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-16 md:py-24 px-4 bg-[#F7F7F7]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#1F3A5F]/10 text-[#1F3A5F] px-4 py-2 rounded-full text-sm font-bold mb-4">
                <Star className="w-4 h-4" />
                {language === 'nl' ? 'ONZE CULTUUR' : 'OUR CULTURE'}
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-[#2A2A2A]">
                {language === 'nl' ? 'Wat ons drijft' : 'What drives us'}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {values.map((value, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-[#E5E5E5] hover:border-[#2E7D32]/30 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#2E7D32] to-[#1F3A5F] rounded-xl flex items-center justify-center flex-shrink-0">
                      <value.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#2A2A2A] mb-2">{value.title}</h3>
                      <p className="text-[#9E9E9E] leading-relaxed">{value.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Open Positions Section */}
        <section id="vacatures" className="py-16 md:py-24 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#2E7D32]/10 text-[#2E7D32] px-4 py-2 rounded-full text-sm font-bold mb-4">
                <Briefcase className="w-4 h-4" />
                {language === 'nl' ? 'VACATURES' : 'VACANCIES'}
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-[#2A2A2A] mb-4">
                {language === 'nl' ? 'Open posities' : 'Open positions'}
              </h2>
              <p className="text-lg text-[#9E9E9E]">
                {language === 'nl' 
                  ? 'Bekijk onze vacatures en solliciteer direct'
                  : 'View our vacancies and apply directly'}
              </p>
            </div>

            <div className="space-y-4">
              {openings.map((job, i) => (
                <a
                  key={i}
                  href={`mailto:jobs@swipejewoning.nl?subject=Sollicitatie: ${job.title}`}
                  className="block bg-[#F7F7F7] rounded-2xl p-6 hover:shadow-lg transition-all hover:-translate-y-1 border border-transparent hover:border-[#2E7D32]/30 group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-[#2A2A2A] group-hover:text-[#2E7D32] transition-colors">
                          {job.title}
                        </h3>
                        {job.urgent && (
                          <span className="bg-[#C04A4A] text-white text-xs font-bold px-2 py-1 rounded-full">
                            URGENT
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span className="bg-white px-3 py-1 rounded-full text-[#2A2A2A] font-medium">{job.department}</span>
                        <span className="text-[#9E9E9E] flex items-center gap-1">
                          <MapPin className="w-4 h-4" /> {job.location}
                        </span>
                        <span className="text-[#9E9E9E]">{job.type}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-6 h-6 text-[#9E9E9E] group-hover:text-[#2E7D32] transition-colors" />
                  </div>
                </a>
              ))}
            </div>

            {/* Open Application */}
            <div className="mt-12 bg-gradient-to-r from-[#1F3A5F] to-[#2E7D32] rounded-3xl p-8 text-center text-white">
              <Building2 className="w-12 h-12 mx-auto mb-4 opacity-80" />
              <h3 className="text-2xl font-bold mb-2">
                {language === 'nl' ? 'Geen passende vacature?' : 'No suitable vacancy?'}
              </h3>
              <p className="text-white/80 mb-6 max-w-md mx-auto">
                {language === 'nl' 
                  ? 'Stuur een open sollicitatie! We zijn altijd op zoek naar talent.'
                  : 'Send an open application! We are always looking for talent.'}
              </p>
              <a href="mailto:jobs@swipejewoning.nl">
                <Button className="bg-white text-[#1F3A5F] hover:bg-white/90 h-12 px-8 rounded-full font-bold">
                  <Briefcase className="w-5 h-5 mr-2" />
                  jobs@swipejewoning.nl
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 bg-[#F7F7F7]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-black text-[#2A2A2A] mb-4">
              {language === 'nl' ? 'Klaar om te starten?' : 'Ready to start?'}
            </h2>
            <p className="text-lg text-[#9E9E9E] mb-8">
              {language === 'nl' 
                ? 'Word onderdeel van ons team en help duizenden mensen hun droomwoning vinden.'
                : 'Become part of our team and help thousands of people find their dream home.'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="mailto:jobs@swipejewoning.nl">
                <Button className="bg-[#2E7D32] hover:bg-[#256B2B] text-white h-14 px-8 rounded-full font-bold text-lg">
                  {language === 'nl' ? 'Solliciteer nu' : 'Apply now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CareersPage;
